<script setup>
import EyeIcon from '@iconify-vue/ph/eye';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { standard, premium } = defineProps({
    standard: {
        type: Boolean,
        required: false,
    },
    premium: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route('front.projectManagement.seo.apikomContents', {
                        tenant: props.row.original.id,
                    }),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'total',
            header: () => 'Contenus',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.total,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalStandard',
            header: () => 'Standard',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.totalStandard,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalPremium',
            header: () => 'Premium',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.totalPremium,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalToProcess',
            header: () => 'À traiter',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.totalToProcess,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalPended',
            header: () => 'En attente',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.totalPended,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'maxDate',
            header: () => 'Date max en cours',
            cell: (props) =>
                props.row.original.maxDate
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.maxDate,
                              false,
                              false,
                              false,
                              true,
                              true,
                          ),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'maxProcessed',
            header: () => 'Date max terminée',
            cell: (props) =>
                props.row.original.maxProcessed
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.maxProcessed,
                              false,
                              false,
                              false,
                              true,
                              true,
                          ),
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Voir',
                                icon: EyeIcon,
                                href: route(
                                    'front.projectManagement.seo.apikomContents',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.seo.postApikomContents'),
    data: {
        standard,
        premium,
    },
    columnVisibility: {
        totalStandard: !(standard || premium),
        totalPremium: !(standard || premium),
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    />
</template>
